import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Startup Services', path: '/startup-services' },
    { name: 'GST', path: '/gst' },
    { name: 'Registration', path: '/registration' },
    { name: 'Compliances', path: '/compliances' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-2' : 'bg-white/95 backdrop-blur-sm py-4'
      }`}
      data-testid="navbar"
    >
      <div className="container-custom">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_gk-solutions-dev/artifacts/87lzsvrc_627675338_122100946359246119_1517692003938856282_n.jpg" 
              alt="GK Legal Solutions" 
              className="h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              link.children ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger className="flex items-center gap-1 font-body text-sm font-medium text-slate-700 hover:text-primary transition-colors" data-testid="nav-services-dropdown">
                    {link.name}
                    <ChevronDown className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="w-48">
                    {link.children.map((child) => (
                      <DropdownMenuItem key={child.path} asChild>
                        <Link 
                          to={child.path} 
                          className="w-full cursor-pointer"
                          data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {child.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-body text-sm font-medium transition-colors ${
                    isActive(link.path) ? 'text-primary' : 'text-slate-700 hover:text-primary'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:+919820669943" className="flex items-center gap-2 text-slate-600 hover:text-primary transition-colors">
              <Phone className="w-4 h-4" />
              <span className="font-body text-sm font-medium">+91 98206 69943</span>
            </a>
            <Link to="/contact">
              <Button className="btn-primary" data-testid="nav-get-started-btn">
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 p-0">
              <div className="flex flex-col h-full">
                <div className="p-6 border-b">
                  <div className="flex items-center gap-2">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_gk-solutions-dev/artifacts/87lzsvrc_627675338_122100946359246119_1517692003938856282_n.jpg" 
                      alt="GK Legal Solutions" 
                      className="h-10 w-auto object-contain"
                    />
                  </div>
                </div>
                <nav className="flex-1 p-6">
                  <div className="flex flex-col gap-2">
                    {navLinks.map((link) => (
                      link.children ? (
                        <div key={link.name} className="py-2">
                          <span className="font-body text-sm font-semibold text-slate-500 uppercase tracking-wider">
                            {link.name}
                          </span>
                          <div className="mt-2 ml-2 flex flex-col gap-1">
                            {link.children.map((child) => (
                              <Link
                                key={child.path}
                                to={child.path}
                                onClick={() => setMobileOpen(false)}
                                className="font-body text-base text-slate-700 hover:text-primary py-2 transition-colors"
                                data-testid={`mobile-nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                {child.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <Link
                          key={link.path}
                          to={link.path}
                          onClick={() => setMobileOpen(false)}
                          className={`font-body text-base py-3 transition-colors ${
                            isActive(link.path) ? 'text-primary font-semibold' : 'text-slate-700 hover:text-primary'
                          }`}
                          data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                        >
                          {link.name}
                        </Link>
                      )
                    ))}
                  </div>
                </nav>
                <div className="p-6 border-t bg-slate-50">
                  <a href="tel:+919820669943" className="flex items-center gap-2 text-slate-600 mb-4">
                    <Phone className="w-4 h-4" />
                    <span className="font-body text-sm font-medium">+91 98206 69943</span>
                  </a>
                  <Link to="/contact" onClick={() => setMobileOpen(false)}>
                    <Button className="btn-primary w-full" data-testid="mobile-get-started-btn">
                      Get Started
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
